<script setup lang="ts">
import { usePage } from "@inertiajs/vue3"
import { computed, ref } from "vue"
import { Pencil, Search, Users } from "lucide-vue-next"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { toHuman } from "@/helpers/formatters/currency"
import AppLayout from "@/layouts/AppLayout.vue"
import EditEmployeeDialog from "./EditEmployeeDialog.vue"

defineOptions({
    layout: [AppLayout],
})

type Branch = {
    id: number
    name: string
}

type Employee = {
    id: number
    employee_number: string
    branch_id: number | null
    branch: Branch | null
    name: string
    title: string
    status: string
    email: string
    mobile: string | null
    address: string
    birthday: string | null
    date_started_at: string | null
    sss_number: string | null
    prn_number: string | null
    mid_number: string | null
    tin_number: string | null
    is_active: boolean
    has_benefits: boolean
    rate_per_hour: number
    full_shift_rate: number
}

type Props = {
    employees: { data: Employee[] }
}

type StatusFilter = "all" | "active" | "inactive"

const props = defineProps<Props>()
const page = usePage()

const branches = computed(() => (page.props.branches as Branch[]) ?? [])
const query = ref("")
const statusFilter = ref<StatusFilter>("all")
const dialogOpen = ref(false)
const editingEmployee = ref<Employee | null>(null)

const counts = computed(() => {
    const employees = props.employees.data

    return {
        all: employees.length,
        active: employees.filter((employee) => employee.is_active).length,
        inactive: employees.filter((employee) => !employee.is_active).length,
    }
})

const filteredEmployees = computed(() => {
    const search = query.value.trim().toLowerCase()

    return props.employees.data.filter((employee) => {
        if (statusFilter.value === "active" && !employee.is_active) {
            return false
        }

        if (statusFilter.value === "inactive" && employee.is_active) {
            return false
        }

        if (!search) {
            return true
        }

        return [employee.employee_number, employee.name, employee.title, employee.email, employee.mobile]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(search))
    })
})

function initials(name: string) {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? "")
        .join("")
}

function openEdit(employee: Employee) {
    editingEmployee.value = employee
    dialogOpen.value = true
}
</script>

<template>
    <div class="min-h-screen bg-[#f3f1ea] -mx-4 sm:-mx-6 lg:-mx-8">
        <div class="bg-[rgb(181_115_5)] text-white">
            <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <p class="text-sm font-medium uppercase tracking-[0.2em] text-white/70">Staff</p>
                <h1 class="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Employees</h1>
                <p class="mt-2 max-w-2xl text-white/80">
                    Names, titles, pay, and government IDs for everyone on the floor.
                </p>
            </div>
        </div>

        <div class="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div class="relative max-w-md flex-1">
                    <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input v-model="query" class="pl-9" placeholder="Search by number, name, title, or email" />
                </div>
                <div class="flex gap-1 rounded-lg border bg-white p-1">
                    <Button
                        type="button"
                        size="sm"
                        :variant="statusFilter === 'all' ? 'default' : 'ghost'"
                        @click="statusFilter = 'all'"
                    >
                        All ({{ counts.all }})
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        :variant="statusFilter === 'active' ? 'default' : 'ghost'"
                        @click="statusFilter = 'active'"
                    >
                        Active ({{ counts.active }})
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        :variant="statusFilter === 'inactive' ? 'default' : 'ghost'"
                        @click="statusFilter = 'inactive'"
                    >
                        Inactive ({{ counts.inactive }})
                    </Button>
                </div>
            </div>

            <div class="overflow-hidden rounded-xl border bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Number</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Branch</TableHead>
                            <TableHead>Daily rate</TableHead>
                            <TableHead class="w-[80px] text-right">
                                <span class="sr-only">Edit</span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="employee in filteredEmployees" :key="employee.id">
                            <TableCell class="font-mono text-sm tracking-wide text-gray-700">
                                {{ employee.employee_number }}
                            </TableCell>
                            <TableCell>
                                <div class="flex items-center gap-3">
                                    <Avatar class="h-9 w-9">
                                        <AvatarFallback>{{ initials(employee.name) }}</AvatarFallback>
                                    </Avatar>
                                    <div class="min-w-0">
                                        <p class="truncate font-medium text-gray-900">{{ employee.name }}</p>
                                        <p class="truncate text-xs text-muted-foreground">{{ employee.email }}</p>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell class="text-sm text-gray-700">{{ employee.title }}</TableCell>
                            <TableCell>
                                <div class="flex flex-wrap items-center gap-2">
                                    <span class="text-sm text-gray-700">{{ employee.status }}</span>
                                    <Badge :variant="employee.is_active ? 'default' : 'secondary'">
                                        {{ employee.is_active ? "Active" : "Inactive" }}
                                    </Badge>
                                </div>
                            </TableCell>
                            <TableCell class="text-sm text-muted-foreground">
                                {{ employee.branch?.name ?? "—" }}
                            </TableCell>
                            <TableCell class="text-sm font-medium">
                                {{ toHuman(employee.full_shift_rate) }}
                            </TableCell>
                            <TableCell class="text-right">
                                <Button variant="ghost" size="icon" class="h-8 w-8" @click="openEdit(employee)">
                                    <Pencil class="h-3.5 w-3.5" />
                                    <span class="sr-only">Edit {{ employee.name }}</span>
                                </Button>
                            </TableCell>
                        </TableRow>
                        <TableRow v-if="filteredEmployees.length === 0">
                            <TableCell colspan="7" class="py-12 text-center text-sm text-muted-foreground">
                                <Users class="mx-auto mb-2 h-8 w-8 opacity-40" />
                                No employees match this search.
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>

        <EditEmployeeDialog v-model:open="dialogOpen" :employee="editingEmployee" :branches="branches" />
    </div>
</template>
